import {
  HabitSchedule,
  PlannedOccurrence,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { getFrequencyDueDateBySchedule } from './get-frequency-due-date-by-schedule';
import { getFrequencyPlanningBySchedule } from './get-frequency-planning-by-schedule';
import { getRruleDueDateBySchedule } from './get-rrule-due-date-by-schedule';
import { getRrulePlanningBySchedule } from './get-rrule-planning-by-schedule';

export type ScheduleDueDatesOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
  today: Timestamp;
  weekStartsOn: WeekDays;
  uncompletedOnly?: boolean;
  sinceLastEntryOnly?: boolean;
  strictEndDate?: boolean;
};

// rrule expansion dominates list renders; keyed on the schedule object —
// identity-stable for unchanged docs — so the cache self-invalidates on edits.
const planningCache = new WeakMap<
  HabitSchedule,
  Map<string, PlannedOccurrence[]>
>();
const PLANNING_CACHE_MAX_VARIANTS = 8;

const toPlanningCacheKey = ({
  startDate,
  endDate,
  today,
  weekStartsOn,
  uncompletedOnly,
  sinceLastEntryOnly,
  strictEndDate,
}: ScheduleDueDatesOptions) =>
  [
    startDate.getTime(),
    endDate.getTime(),
    today.getTime(),
    weekStartsOn,
    uncompletedOnly ? 1 : 0,
    sinceLastEntryOnly ? 1 : 0,
    strictEndDate ? 1 : 0,
  ].join(':');

export const getSchedulePlanning = (
  schedule: HabitSchedule,
  options: ScheduleDueDatesOptions,
): PlannedOccurrence[] => {
  const cacheKey = toPlanningCacheKey(options);
  const cachedVariants = planningCache.get(schedule);
  const cached = cachedVariants?.get(cacheKey);
  if (cached) {
    // fresh array instance so caller-side mutations cannot corrupt the cache
    return cached.slice();
  }

  const {
    startDate,
    endDate,
    today,
    weekStartsOn,
    uncompletedOnly,
    sinceLastEntryOnly,
    strictEndDate,
  } = options;

  const result = schedule.frequency?.count
    ? getFrequencyPlanningBySchedule(schedule, {
        startDate,
        endDate,
        today,
        weekStartsOn,
        uncompletedOnly,
        sinceLastEntryOnly,
      })
    : getRrulePlanningBySchedule(schedule, {
        startDate,
        endDate,
        weekStartsOn,
        uncompletedOnly,
        sinceLastEntryOnly,
        strictEndDate,
      });

  const variants = cachedVariants ?? new Map<string, PlannedOccurrence[]>();
  if (!cachedVariants) {
    planningCache.set(schedule, variants);
  }
  if (variants.size >= PLANNING_CACHE_MAX_VARIANTS) {
    variants.clear();
  }
  variants.set(cacheKey, result.slice());

  return result;
};

export type ScheduleDueDateOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
};

export const getScheduleDueDate = (
  schedule: HabitSchedule,
  { today, weekStartsOn }: ScheduleDueDateOptions,
) => {
  if (schedule.frequency?.count) {
    return getFrequencyDueDateBySchedule(schedule, { today, weekStartsOn });
  }

  return getRruleDueDateBySchedule(schedule, { today, weekStartsOn });
};
